import { Vector3 } from "three";
import { tolerances } from "./constants";

// A handle whose drag axis or drag plane is degenerate from the current viewpoint is worse than
// no handle at all: dragging it produces no visible change, or the shape collapses to a line.
// Both predicates are sign-agnostic and normalize their inputs — plane normals built via
// Plane.setFromNormalAndCoplanarPoint and Placement3d.axis read from JSON are not unit length.
// A degenerate input answers "false" (keep the handle), so a missing camera or a zero-length
// axis can never blank out every handle.

const isUsable = (vector: Vector3): boolean => vector.lengthSq() > 1e-10;

// True when the view looks along the axis (either way round), so movement along it is invisible.
export function isViewAlongAxis(viewDirection: Vector3, axis: Vector3): boolean {
    if (!isUsable(viewDirection) || !isUsable(axis)) return false;

    const cosAngle = Math.abs(
        viewDirection.clone().normalize().dot(axis.clone().normalize())
    );

    return cosAngle >= Math.cos(tolerances.editHandleViewAlongAxis);
}

// True when the view lies (nearly) in the plane, so the plane is seen edge-on.
export function isPlaneEdgeOn(viewDirection: Vector3, planeNormal: Vector3): boolean {
    if (!isUsable(viewDirection) || !isUsable(planeNormal)) return false;

    const cosAngleToNormal = Math.abs(
        viewDirection.clone().normalize().dot(planeNormal.clone().normalize())
    );

    return cosAngleToNormal <= Math.sin(tolerances.editHandlePlaneEdgeOn);
}
